/**
 * ─── THE one place to change the app's colours ──────────────────────────────
 *
 * Change `DEFAULT_BRAND` (or a brand's `primary` / `accent`) and every antd
 * component, every semantic Tailwind class (bg-primary, text-accent, …) and every
 * `useTheme().tokens` consumer follows. antd's algorithm derives the full
 * palette (hover/active/bg/border/text, light AND dark) from each seed.
 */

import { theme as antdTheme } from "antd";

export const THEME_MODES = Object.freeze({
  LIGHT: "light",
  DARK: "dark",
  SYSTEM: "system",
});

// Brand presets — two seeds each, everything else is derived:
//   primary → antd `colorPrimary` + links: buttons, active states, key data
//   accent  → the brand's secondary colour: highlights, secondary series/badges
//             (named "accent" because `text-secondary` already means muted text)
// Seeds are antd preset palette colours so the derived scales match the antd
// Figma kit. Accents are chosen to pair with the primary and to stay clear of the
// status colours (green success / amber warning / red error).
export const BRANDS = Object.freeze({
  blue: { label: "Blue", primary: "#1677ff", accent: "#722ed1" }, // + purple
  magenta: { label: "Magenta", primary: "#eb2f96", accent: "#2f54eb" }, // + geekblue
  purple: { label: "Purple", primary: "#722ed1", accent: "#eb2f96" }, // + magenta
  cyan: { label: "Cyan", primary: "#13c2c2", accent: "#2f54eb" }, // + geekblue
  green: { label: "Green", primary: "#52c41a", accent: "#13c2c2" }, // + cyan
  volcano: { label: "Volcano", primary: "#fa541c", accent: "#2f54eb" }, // + geekblue
});

// Status colours — one seed each, shared by every brand and mode. antd derives
// the full scale from each (hover / bg / border / text, light AND dark), which
// feeds antd's status props (`Tag color="success"`, `Alert type="error"`,
// `Typography type="warning"`) and the Tailwind classes (`text-success`,
// `bg-error-bg`, `border-warning-border`, …).
// Taken from antd's own default seed, so they track antd's palette. To recolour
// a status app-wide, replace its value with another antd palette colour (e.g.
// `"#13c2c2"` cyan-6) — see https://ant.design/docs/spec/colors.
const { defaultSeed } = antdTheme;
export const STATUS_COLORS = Object.freeze({
  success: defaultSeed.colorSuccess, // #52c41a green-6
  warning: defaultSeed.colorWarning, // #faad14 gold-6
  error: defaultSeed.colorError, // #ff4d4f red-5
  info: defaultSeed.colorInfo, // #1677ff blue-6
});

export const DEFAULT_BRAND = "blue";
export const DEFAULT_MODE = THEME_MODES.LIGHT;

// Main sidebar navy — the same in light and dark mode.
export const SIDEBAR_BG = "#001529";

// The app's dark base colour. In dark mode antd derives every surface (page,
// card, modal, popover) from this seed by lightening it, keeping its hue and
// saturation — and the tint grows with each lighter layer. So it's a near-neutral
// slate with only a hint of cool: a navy base (#101828) turned cards and borders
// visibly blue (#1c2945 / #365187), which was too loud next to the content.
//   page #111317 · cards #22262e · modals #2b303a · dividers #3a414f
export const DARK_BG_BASE = "#111317";

// Seed overrides per mode, applied on top of the antd algorithm. Policy: antd's
// default token system only — add SEEDS here (never alias/derived values, never
// per-component hex), so antd still derives every shade itself.
export const MODE_TOKEN_OVERRIDES = Object.freeze({
  light: {},
  dark: { colorBgBase: DARK_BG_BASE },
});

// antd component-level tokens (per mode). Prefer these over global `.ant-*` CSS.
export const COMPONENT_TOKENS = Object.freeze({
  light: {},
  dark: {},
});

/**
 * Theme preview gate. Until the whole app is migrated to semantic tokens, mode
 * and brand switching are only available in local development (`gatsby develop`)
 * or when `GATSBY_THEME_PREVIEW=true`. Production builds without the flag always
 * render light + DEFAULT_BRAND (today's look).
 */
export const THEME_PREVIEW_ENABLED =
  process.env.GATSBY_THEME_PREVIEW === "true" || process.env.NODE_ENV === "development";

/**
 * Routes whose UI has been migrated to semantic tokens and is safe to render
 * dark. Anything else stays light even when dark is selected, so un-migrated
 * modules never show white-on-dark breakage. Add a module's route prefix here
 * once it is migrated; delete the list once every module is.
 */
export const DARK_READY_ROUTES = Object.freeze(["/ottovin-reports", "/admin-dashboard"]);

export const THEME_STORAGE_KEY = "eai-theme";
export const THEME_STYLE_ELEMENT_ID = "eai-theme-vars";

export const isDarkReadyRoute = (pathname = "") =>
  DARK_READY_ROUTES.some(prefix => pathname === prefix || pathname.startsWith(`${prefix}/`));

/**
 * Resolve what actually renders from the stored preference + context.
 * Mirrored (in plain ES5) by the pre-paint script in `themeScript.js` — keep the
 * two in sync.
 */
export const resolveTheme = ({ preference = {}, pathname = "", prefersDark = false } = {}) => {
  if (!THEME_PREVIEW_ENABLED) return { mode: THEME_MODES.LIGHT, brand: DEFAULT_BRAND };
  const brand = BRANDS[preference.brand] ? preference.brand : DEFAULT_BRAND;
  const wanted =
    preference.mode === THEME_MODES.SYSTEM
      ? prefersDark
        ? THEME_MODES.DARK
        : THEME_MODES.LIGHT
      : preference.mode || DEFAULT_MODE;
  const mode =
    wanted === THEME_MODES.DARK && isDarkReadyRoute(pathname)
      ? THEME_MODES.DARK
      : THEME_MODES.LIGHT;
  return { mode, brand };
};
